import { Box, Stack, Typography } from '@unity/cloud-ui';
import { Accordion } from '../../accordion';
import { Code } from '../../code';
import { Field } from '../../field';
import type { OpenAPIResponse, OpenAPISchemaProperty } from '../types/types';

interface ResponseSchemaProps {
  statusCode: string;
  response: OpenAPIResponse;
  schema: OpenAPISchemaProperty | undefined;
  contentType?: string;
  t: (key: string) => string;
}

export const ResponseSchemaAccordion = ({ statusCode, response, schema, contentType, t }: ResponseSchemaProps) => {
  const statusCodeNum = parseInt(statusCode, 10);
  let variant: 'success' | 'warning' | 'error' | undefined;

  if (statusCodeNum >= 200 && statusCodeNum < 300) {
    variant = 'success';
  } else if (statusCodeNum >= 300 && statusCodeNum < 400) {
    variant = 'warning';
  } else if (statusCodeNum >= 400) {
    variant = 'error';
  }

  return (
    <Accordion
      blockExpansion={schema && schema.properties ? false : true}
      key={statusCode}
      variant={variant}
      title={
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Typography component="div" sx={{ flex: 1 }}>
            <Code>{statusCode}</Code>
          </Typography>
          <Typography component="div" sx={{ flex: 1 }}>
            {response?.description || t('mdx:openapi:no-description')}
          </Typography>
        </Box>
      }
      sxOverride={{ padding: 0, borderTop: 'none' }}
    >
      {(contentType || (schema && schema.properties)) && (
        <Box sx={{ p: 2 }}>
          {contentType && (
            <Stack sx={{ mb: 1 }} direction={'row'} alignItems={'center'} spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {t('mdx:openapi:media-type')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Code>{contentType}</Code>
              </Typography>
            </Stack>
          )}
          {schema && schema.properties && (
            <>
              {Object.entries(schema.properties).map(([propName, propSchema]) => {
                const exampleValue =
                  typeof propSchema === 'object' &&
                  propSchema !== null &&
                  'example' in propSchema &&
                  (propSchema as OpenAPISchemaProperty).example !== undefined
                    ? `${(propSchema as OpenAPISchemaProperty).example}`
                    : undefined;

                const description =
                  typeof propSchema === 'object' && propSchema !== null && 'description' in propSchema
                    ? (propSchema as OpenAPISchemaProperty).description || t('mdx:openapi:no-description')
                    : t('mdx:openapi:no-description');

                const type =
                  typeof propSchema === 'object' && propSchema !== null
                    ? (propSchema as OpenAPISchemaProperty).type
                    : undefined;

                return (
                  <Box key={propName} sx={{ mb: 2 }}>
                    <Field
                      title={propName}
                      type={type}
                      required={schema.required?.includes(propName)}
                      example={exampleValue}
                    >
                      {description}
                    </Field>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      )}
    </Accordion>
  );
};
